/**
 * Mitspieler einer Partie: wer dabei ist, mit welcher Rolle, und – für
 * Administratoren – die Möglichkeit, weitere Benutzer aufzunehmen oder wieder
 * zu entfernen.
 *
 * Steht an zwei Stellen: als Dialog in der Partienübersicht (vor dem Öffnen)
 * und als Karte in den Einstellungen einer laufenden Partie. Beide teilen sich
 * diese Ansicht, damit eine Änderung an der Berechtigungslogik nicht an zwei
 * Stellen gepflegt werden muss.
 *
 * Die eigene Rolle bekommt der Aufrufer nicht mitgegeben – sie steht aus
 * Sicht der Liste (`GameListEntry`) nicht immer fest, etwa wenn die Partie
 * schon geöffnet ist. Der Baustein lädt die Mitgliederliste deshalb selbst und
 * bestimmt daraus, was er anzeigen darf – dieselbe Prüfung, die der Server
 * ohnehin durchsetzt.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Notice, Select } from './ui';
import { api, type GameMemberEntry, type GameMembers, type GameRole } from '../lib/api';
import { useAuth } from '../store/auth';

const ROLE_OPTIONS: { value: GameRole; label: string; hint: string }[] = [
  { value: 'TECHNICIAN', label: 'Techniker', hint: 'Baut mit: Technik, Zeitsteuerung, Spielstände.' },
  { value: 'ADMIN', label: 'Administrator', hint: 'Darf alles, auch Mitglieder verwalten.' },
  { value: 'SUPPORT', label: 'Support', hint: 'Nur Tickets, Kunden und Verträge.' },
  { value: 'CUSTOMER', label: 'Kunde', hint: 'Nur zusehen, keine Aktionen.' },
];

export function MembersPanel({ gameId }: { gameId: string }) {
  const [data, setData] = useState<GameMembers | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<GameRole>('TECHNICIAN');
  const [busy, setBusy] = useState(false);
  const currentUser = useAuth((s) => s.user);

  const load = useCallback(async () => {
    try {
      setData(await api.listMembers(gameId));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [gameId]);

  useEffect(() => {
    setData(null);
    setError(null);
    void load();
  }, [load]);

  const isAdmin =
    data?.owner.id === currentUser?.id || Boolean(data?.members.some((m) => m.id === currentUser?.id && m.role === 'ADMIN'));

  const invite = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.inviteMember(gameId, email.trim(), role);
      setEmail('');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (member: GameMemberEntry) => {
    setBusy(true);
    setError(null);
    try {
      await api.removeMember(gameId, member.id);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 text-sm">
      <p className="text-panel-500 dark:text-panel-400">
        Lade Freunde ein, gemeinsam an dieser Partie zu bauen. Jede Rolle darf etwas anderes – Techniker bauen mit, Support
        kümmert sich nur um Tickets und Kunden, Kunden sehen lediglich zu.
      </p>

      {error && <Notice tone="danger">{error}</Notice>}

      {!data && !error && <p className="text-xs text-panel-400">Lädt…</p>}

      {data && (
        <ul className="divide-y divider">
          {[data.owner, ...data.members].map((m) => {
            const isSelf = m.id === currentUser?.id;
            return (
              <li key={m.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {m.displayName} {isSelf && <span className="text-panel-400">(du)</span>}
                  </p>
                  <p className="truncate text-xs text-panel-500 dark:text-panel-400">
                    {m.email} · {m.roleLabel}
                  </p>
                </div>
                {m.id !== data.owner.id && (isAdmin || isSelf) && (
                  <Button variant="ghost" size="sm" loading={busy} onClick={() => remove(m)}>
                    {isSelf ? 'Verlassen' : 'Entfernen'}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {isAdmin && (
        <div className="space-y-2 border-t divider pt-4">
          <p className="label">Mitspieler einladen</p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[12rem] flex-1">
              <Input
                label="E-Mail-Adresse"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="freund@example.com"
              />
            </div>
            <div className="w-40">
              <Select label="Rolle" value={role} onChange={(e) => setRole(e.target.value as GameRole)}>
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Select>
            </div>
            <Button variant="primary" loading={busy} disabled={!email.trim()} onClick={invite}>
              Einladen
            </Button>
          </div>
          <p className="text-xs text-panel-500 dark:text-panel-400">
            {ROLE_OPTIONS.find((r) => r.value === role)?.hint} Eingeladen werden kann nur, wer bereits ein Konto hat.
          </p>
        </div>
      )}
    </div>
  );
}
